import {ModerationUser} from "interfaces";
import {Apps, MoodName, Moods} from "enums";
import {
    CommonSearchPayload,
    SearchUserPayloadBloom,
    SearchUserPayloadFlava,
    SearchUserPayloadInstinct,
    SearchUserPayloadSpark
} from "./interfaces/types";
import {
    convertAttractivePhotoPayloadFlavaToBloom,
    convertPhotoPayloadSparkToBloom,
    PhotoPayloadBloom
} from "./interfaces/photoPayloadInstinct";
import {convertFlavaPhotoToBloom, convertPhotoToBloom, groupMetaTags, mapBucketData, mapCommonFields} from "./utils";
import {toDate} from "./helpers";
import {VerificationRejectReasons} from "enums/VerificationRejectReasons";
import {UserStateChangeReason} from "enums/UserStatusChangeReason";

// Helper function to convert common parts
const convertCommonParts = (payload: CommonSearchPayload, app: Apps): Partial<ModerationUser> => {
    return {
        app: payload.app,
        isCouple: payload.isCouple,
        metaTags: groupMetaTags(payload.metaTags ?? payload.interests ?? payload.passions ?? [], app),
        premiumType: payload.premiumType,
        aboutAnswers: payload.aboutAnswers ?? [],
        wasReviewed: payload.wasReviewed,
        reviewedDate: toDate(payload.reviewedDate, app),
        reviewTags: payload.reviewTags?.map(tag => ({
            ...tag,
            date: toDate(tag.date, app)
        })) ?? [],
        reports: payload.reports,
        outgoingReports: payload.outgoingReports,
        outgoingReportsCount: payload.outgoingReportsCount,
        version: payload.version,
        verificationStatus: payload.verificationStatus,
        verificationPhoto: payload.verificationPhoto ? {
            ...payload.verificationPhoto,
            link: payload.verificationPhoto.url
        } : undefined,
        subscriptionHistory: payload.subscriptionHistory?.map(sub => ({
            ...sub,
            date: toDate(sub.date, app),
            expiresDate: toDate(sub.expiresDate, app),
            provider: sub.provider
        })) ?? [],
        countryFlag: payload.countryFlag,
        countryName: payload.countryName,
        platform: payload.platform,
        loginMethods: payload.loginMethods,
        statusHistory: payload.statusHistory?.map(status => ({
            ...status,
            reason: status.reason as UserStateChangeReason,
            date: toDate(status.date, app),
        })) ?? [],
        reviewer: payload.reviewer,
        verifier: payload.verifier,
        maxDistanceFilter: payload.maxDistanceFilter,
        boostBuckets: mapBucketData(payload.boostBuckets, app),
        greetingBuckets: mapBucketData(payload.greetingBuckets, app),
        verificationRejectReason: payload.verificationDenyReason as VerificationRejectReasons,
        verificationHistory: payload.verificationHistory?.map(history => ({
            verificationEndDate: toDate(history.verificationEndDate, app),
            verificationPhoto: history.verificationPhoto ? {
                ...history.verificationPhoto,
                link: history.verificationPhoto.url
            } : undefined,
            verificationStartDate: toDate(history.verificationStartDate, app),
            verificationStatus: history.verificationStatus,
            verificationType: history.verificationType,
            verifier: history.verifier,
            rejectReason: history.verificationDenyReason as VerificationRejectReasons
        })) ?? [],
        verificationStartDate: toDate(payload.verificationStartDate, app),
        verificationEndDate: toDate(payload.verificationEndDate, app),
        devices: payload.devices?.map(device => ({
            device: device.device,
            lastActivityDate: toDate(device.lastActivityDate, app)
        })) ?? [],
        photosWithDuplicateHashes: payload.photosWithDuplicateHashes,
        ipAddresses: payload.ipAddresses?.map(ip => ({
            ...ip,
            lastUsageDate: toDate(ip.lastUsageDate, app)
        })),
        isAboutVisible: !payload.isHiddenAbout,
        customIcebreakers: payload.customIcebreakers,
        duplicateFaces: payload.duplicateFaces,
        lastFcmTokenRefresh: toDate(payload.lastFcmTokenRefresh, app),
        appFeatures: payload.appFeatures,
        testUpgrades: payload.testUpgrades
    };
};

export const convertToSearchBloomToModerationUser = (user: SearchUserPayloadBloom) => {
    return {
        id: user.id,
        name: user.name,
        gender: user.gender?.toString(),
        sexuality: user.sexuality.toString(),
        about: user.about,
        distance: user.distance,
        age: user.age,
        onlineDate: new Date(user.userStatusDate),
        photos: [...(user.avatarV2 ? [user.avatarV2] : []), ...user.photosV2],
        interests: user.moods.map(m => MoodName[m.toString() as '1' | '2' | '3']),
        birthday: new Date(user.birthday),
        energy: {
            refillDate: user.energy.lastRefillDate ? new Date(user.energy.lastRefillDate) : undefined,
            amount: user.energy.likes
        },
        ...mapCommonFields(user, Apps.Bloom),
        ...convertCommonParts(user, Apps.Bloom)
    } as ModerationUser;
};

export const convertToSearchSparkToModerationUser = (user: SearchUserPayloadSpark) => {
    return {
        id: user.id,
        name: user.name,
        gender: user.gender?.toString(),
        sexuality: user.sexuality?.toString(),
        about: user.bio,
        distance: user.maxDistanceFilter,
        onlineDate: new Date(user.connectedTime),
        energy: {
            refillDate: user.energy.previousDemandDate ? new Date(user.energy.previousDemandDate) : undefined,
            amount: user.energy.volume
        },
        photos: [
            ...(user.icon ? [convertPhotoPayloadSparkToBloom(user.icon)] : []),
            ...user.images.map(i => convertPhotoPayloadSparkToBloom(i))
        ],
        interests: user.awaitings?.map(m => MoodName[m.toString() as Moods]),
        deletedPhotos: user.deletedPhotos?.map(dp => convertPhotoPayloadSparkToBloom(dp)),
        ...mapCommonFields(user, Apps.Spark),
        ...convertCommonParts(user, Apps.Spark),
    } as ModerationUser;
};

export const convertToSearchFlavaToModerationUser = (user: SearchUserPayloadFlava) => {
    return {
        discoveryCity: user.manualCity,
        id: user.id,
        name: user.firstname,
        gender: user.gender?.toString(),
        about: user.description,
        distance: user.distance,
        onlineDate: new Date(user.wasOnlineDate),
        sexuality: user.sexuality.toString(),
        age: user.age,
        birthday: new Date(user.birthday),
        moods: [],
        photos: [
            ...(user.picV2 ? [convertFlavaPhotoToBloom(user.picV2)] : []),
            ...user.pics.map(convertFlavaPhotoToBloom)
        ],
        attractiveFeatures: user.attractiveFeatures.map(a => ({
            ...a,
            photo: a.photo && convertAttractivePhotoPayloadFlavaToBloom(a.photo)
        })),
        attractedToFeatures: user.attractedToFeatures.map(f => ({text: f.text})),
        deletedPhotos: user.deletedPhotos?.map(convertFlavaPhotoToBloom),
        ...mapCommonFields(user, Apps.Flava),
        ...convertCommonParts(user, Apps.Flava)
    } as ModerationUser;
};

export const convertToSearchInstinctToModerationUser = (user: SearchUserPayloadInstinct) => {
    const photos: PhotoPayloadBloom[] = [
        ...(user.avatarV2 ? [convertPhotoToBloom(user.avatarV2)] : []),
        ...user.photos.map(convertPhotoToBloom)
    ];

    return {
        discoveryCity: user.discoveryCity,
        id: user.id,
        name: user.name,
        gender: user.gender?.toString(),
        sexuality: user.sexuality.toString(),
        about: user.about,
        distance: user.distance,
        age: user.age,
        birthday: new Date(user.birthday),
        onlineDate: new Date(user.onlineDate),
        photos,
        moods: [],
        deletedPhotos: user.deletedPhotos?.map(convertPhotoToBloom),
        ...mapCommonFields(user, Apps.Instinct),
        ...convertCommonParts(user, Apps.Instinct)
    } as ModerationUser;
};
